import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { flushSync } from "react-dom";
import { articlesAPI, tagsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";

import { parseMarkdown } from "../utils/markdown";
import Spinner from "../components/Spinner";
import Button from "../components/Button";
import ApprovedTagChips from "../components/ApprovedTagChips";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import ExitButton from "../components/ExitButton";
import MySpaceButton from "../components/MySpaceButton";
import MyContentButton from "../components/MyContentButton";
import CreateContentButton from "../components/CreateContentButton";
import PdfImportPanel from "../components/PdfImportPanel";
import {
  getEffectiveBodyLimit,
  canUsePdfImport,
  normalizeEmail,
  OWNER_UNLIMITED_EMAIL,
} from "../constants/contentLimits";
import { isEditorialUser as isEditorialAccount } from "../constants/editorialAccounts";
import { formatReadingEstimate } from "../utils/readingTime";
import { useAuthState } from "../hooks/useAuth";
import {
  Eye,
  EyeOff,
  Maximize2,
  Minimize2,
  Share2,
  Globe,
  Lock,
  Users,
  BookOpen,
  Layers,
  X,
  Check,
  Copy,
  Trash2,
  Save,
  FileText,
  ChevronLeft,
  ChevronRight,
  Plus,
  Edit2,
  Trash,
  StickyNote,
  Bookmark,
  MoveDown,
  ArrowLeftRight,
  Sparkles,
} from "lucide-react";
import Prompt from "../components/Prompt";
import ScrollToTopButton from "../components/ScrollToTopButton";
import "./ViewLesson.css";

const EDITORIAL_LESSON_TAG_LIMIT = 2;

const getArticleTags = (article) =>
  Array.isArray(article?.tags) ? article.tags : [];

const areTagListsEqual = (left = [], right = []) =>
  left.length === right.length &&
  left.every((tagSlug, index) => tagSlug === right[index]);

const ViewLesson = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawFolderFromUrl = searchParams.get("folder");
  const folderFromUrl =
    rawFolderFromUrl &&
    rawFolderFromUrl !== "null" &&
    rawFolderFromUrl !== "undefined"
      ? rawFolderFromUrl
      : "";
  const returnToFromUrl = searchParams.get("returnTo");
  const editFromUrl = searchParams.get("edit") === "true";
  const openTakeNotesFromUrl = searchParams.get("openTakeNotes") === "true";
  const takeNotesFromUrl = searchParams.get("takeNotes") === "true";
  const openFlashcardsFromUrl = searchParams.get("openFlashcards") === "true";
  const flashcardsFromUrl = searchParams.get("flashcards") === "true";
  const [article, setArticle] = useState(null);
  const [articles, setArticles] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [highlights, setHighlights] = useState([]);
  const [highlightMode, setHighlightMode] = useState(false);
  const [bookmark, setBookmark] = useState(null);
  const [savedSelection, setSavedSelection] = useState(null);
  const [loadingBookmark, setLoadingBookmark] = useState(false);
  const [fullscreenNotes, setFullscreenNotes] = useState(false);
  const [fullscreenFlashcards, setFullscreenFlashcards] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [cardNotes, setCardNotes] = useState([""]);
  const [originalCardNotes, setOriginalCardNotes] = useState([""]);
  const [originalNoteText, setOriginalNoteText] = useState("");
  const [lessonCopied, setLessonCopied] = useState(false);
  const [flashcardMode, setFlashcardMode] = useState(false);
  const [flashcards, setFlashcards] = useState([{ question: "", answer: "" }]);
  const [savingFlashcards, setSavingFlashcards] = useState(false);
  const [savingIndividualFC, setSavingIndividualFC] = useState({});
  const [originalFlashcards, setOriginalFlashcards] = useState([]);
  const [editMode, setEditMode] = useState(editFromUrl);
  const [editedArticle, setEditedArticle] = useState({
    body: "",
    chapter: "",
    author: "",
    tags: [],
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [deleteStep, setDeleteStep] = useState(0);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const [showSaveFlashcardsPrompt, setShowSaveFlashcardsPrompt] =
    useState(false);
  const [showCancelEditPrompt, setShowCancelEditPrompt] = useState(false);
  const [showDeletePrompt, setShowDeletePrompt] = useState(false);
  const [shouldNavigateToNotes, setShouldNavigateToNotes] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [generatingAiNotes, setGeneratingAiNotes] = useState(false);
  const [loadingNotesPanel, setLoadingNotesPanel] = useState(false);
  const [loadingFlashcardsPanel, setLoadingFlashcardsPanel] = useState(false);
  const [deletingNotes, setDeletingNotes] = useState(false);
  const [deleteNotesStep, setDeleteNotesStep] = useState(0);
  const [savingIndividualNote, setSavingIndividualNote] = useState({});
  const [deletingLesson, setDeletingLesson] = useState(false);
  const [deletingFlashcards, setDeletingFlashcards] = useState(false);
  const [deleteFlashcardsStep, setDeleteFlashcardsStep] = useState(0);
  const [compactSidebar] = useState(true);
  const [compactTakeNotesButtons] = useState(true);
  const [notesEditMode, setNotesEditMode] = useState("cards"); // "cards" or "textarea"
  const toastShownRef = useRef(false);
  const takeNotesTriggeredRef = useRef(false);
  const errorShownRef = useRef(false);
  const lessonJustCreatedRef = useRef(false);
  const emptyLessonDeletedRef = useRef(false);
  const navigationBlockStatePushedRef = useRef(false);
  const lastLoadedLessonIdRef = useRef(null);
  const lessonsCacheRef = useRef(new Map());
  const articleCacheRef = useRef(new Map());
  const bodyRef = useRef(null);
  const textareaRef = useRef(null);
  const notesTextareaRef = useRef(null);
  const notesContainerRef = useRef(null);
  const flashcardsContainerRef = useRef(null);
  const savedScrollPositionRef = useRef(0);
  const flashcardQuestionRefs = useRef([]);
  const flashcardAnswerRefs = useRef([]);
  const { addToast } = useToasts();
  const { user } = useAuthState();
  const isEditorialUser = isEditorialAccount(user);
  const lessonBodyLimit = getEffectiveBodyLimit("lesson", user);
  const showPdfImport = canUsePdfImport(user);
  const [approvedTags, setApprovedTags] = useState([]);
  const [togglingPublic, setTogglingPublic] = useState(false);
  const showEditorialVisibilityToggle =
    isEditorialUser && article?.contentType === "lesson";
  const showEditorialTagEditor = showEditorialVisibilityToggle;
  const showAiNotesButton =
    normalizeEmail(user?.email) === OWNER_UNLIMITED_EMAIL &&
    article?.contentType === "lesson";

  const articleTagChips = useMemo(() => {
    const lessonTags = getArticleTags(article);
    if (!lessonTags.length || !approvedTags.length) {
      return [];
    }

    return approvedTags.filter((tag) => lessonTags.includes(tag.slug));
  }, [approvedTags, article]);
  const readingEstimateLabel = formatReadingEstimate(article);

  useEffect(() => {
    const shouldLoadApprovedTags =
      isEditorialUser ||
      (article?.contentType === "lesson" && getArticleTags(article).length > 0);

    if (!shouldLoadApprovedTags) {
      setApprovedTags([]);
      return;
    }

    let isMounted = true;

    const loadApprovedTags = async () => {
      try {
        const response = await tagsAPI.getApprovedTags("lesson");
        if (isMounted) {
          setApprovedTags(response.data?.tags || []);
        }
      } catch {
        if (isMounted) {
          setApprovedTags([]);
        }
      }
    };

    loadApprovedTags();

    return () => {
      isMounted = false;
    };
  }, [article, isEditorialUser]);
  
  useEffect(() => {
    if (!editMode || !textareaRef.current) {
      return;
    }

    const textarea = textareaRef.current;
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [editMode, editedArticle.body]);

  
  const notes =
    notesEditMode === "cards"
      ? cardNotes
      : noteText
        ? noteText.split("\n\n\n")
        : [""];
  const notesCount = notes.length;
  const currentNotesText = React.useCallback(
    () => (notesEditMode === "cards" ? cardNotes.join("\n\n\n") : noteText),
    [notesEditMode, cardNotes, noteText],
  );

  const originalNotesText = React.useCallback(
    () =>
      notesEditMode === "cards"
        ? originalCardNotes.join("\n\n\n")
        : originalNoteText,
    [notesEditMode, originalCardNotes, originalNoteText],
  );

  const sanitizeReturnPath = React.useCallback((rawPath) => {
    if (!rawPath) return "";
    try {
      const url = new URL(rawPath, window.location.origin);
      const params = new URLSearchParams(url.search);
      params.delete("edit");
      params.delete("takeNotes");
      params.delete("openTakeNotes");
      params.delete("flashcards");
      params.delete("openFlashcards");
      params.delete("returnTo");
      const nextSearch = params.toString();
      return nextSearch ? `${url.pathname}?${nextSearch}` : url.pathname;
    } catch {
      return rawPath;
    }
  }, []);

  const buildLessonPath = React.useCallback(
    (lessonId, extraParams = {}) => {
      const params = new URLSearchParams();

      if (folderFromUrl) {
        params.set("folder", folderFromUrl);
      }

      Object.entries(extraParams).forEach(([key, value]) => {
        if (
          value === undefined ||
          value === null ||
          value === false ||
          value === ""
        ) {
          return;
        }

        params.set(key, String(value));
      });

      const query = params.toString();
      return query ? `/view/lesson/${lessonId}?${query}` : `/view/lesson/${lessonId}`;
    },
    [folderFromUrl],
  );

  const fallbackLessonsPath = `/study/lessons?folder=${encodeURIComponent(folderFromUrl || "")}`;
  const returnPath = sanitizeReturnPath(returnToFromUrl) || fallbackLessonsPath;
  const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  };

  const syncLessonCaches = React.useCallback((nextLesson) => {
    if (!nextLesson?._id) {
      return;
    }

    articleCacheRef.current.set(nextLesson._id, nextLesson);

    lessonsCacheRef.current.forEach((cachedLessons, cacheKey) => {
      if (!Array.isArray(cachedLessons)) {
        return;
      }

      const lessonExists = cachedLessons.some(
        (cachedLesson) => cachedLesson._id === nextLesson._id,
      );
      const nextLessons = lessonExists
        ? cachedLessons.map((cachedLesson) =>
            cachedLesson._id === nextLesson._id
              ? { ...cachedLesson, ...nextLesson }
              : cachedLesson,
          )
        : cachedLessons;

      lessonsCacheRef.current.set(cacheKey, nextLessons);
    });

    setArticles((prev) =>
      prev.map((cachedLesson) =>
        cachedLesson._id === nextLesson._id
          ? { ...cachedLesson, ...nextLesson }
          : cachedLesson,
      ),
    );
  }, []);

  const removeLessonFromCaches = React.useCallback((lessonId) => {
    if (!lessonId) {
      return;
    }

    articleCacheRef.current.delete(lessonId);

    lessonsCacheRef.current.forEach((cachedLessons, cacheKey) => {
      if (!Array.isArray(cachedLessons)) {
        return;
      }

      lessonsCacheRef.current.set(
        cacheKey,
        cachedLessons.filter((cachedLesson) => cachedLesson._id !== lessonId),
      );
    });

    setArticles((prev) =>
      prev.filter((cachedLesson) => cachedLesson._id !== lessonId),
    );
  }, []);

  const invalidateLessonListCaches = React.useCallback(() => {
    lessonsCacheRef.current.clear();
  }, []);

  const scrollFlashcardsPanelToBottom = React.useCallback(() => {
    setTimeout(() => {
      const container = flashcardsContainerRef.current;
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 0);
  }, []);

  const addSelectedTextToFlashcards = React.useCallback((selectedText) => {
    setFlashcards((prev) => {
      const selectedFlashcard = {
        question: "Question",
        answer: selectedText,
      };
      const hasOnlyEmptyDraft =
        prev.length === 1 &&
        !String(prev[0]?.question || "").trim() &&
        !String(prev[0]?.answer || "").trim();

      if (hasOnlyEmptyDraft) {
        return [selectedFlashcard];
      }

      return [...prev, selectedFlashcard];
    });

    scrollFlashcardsPanelToBottom();
  }, [scrollFlashcardsPanelToBottom]);

  const addSelectedTextToCardNotes = React.useCallback((selectedText) => {
    setCardNotes((prev) => {
      const hasOnlyEmptyDraft =
        prev.length === 1 && !String(prev[0] || "").trim();

      if (hasOnlyEmptyDraft) {
        return [selectedText];
      }

      return [...prev, selectedText];
    });
  }, []);

  const handleTextSelection = React.useCallback(async () => {
    if (!article || (!highlightMode && !flashcardMode)) return;

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const lessonBody = bodyRef.current;
    if (!lessonBody || !lessonBody.contains(range.commonAncestorContainer)) {
      return;
    }

    const selectedText = range.toString().trim();
    if (!selectedText) return;

    // Save selection for bookmark
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(lessonBody);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const offset = preSelectionRange.toString().length;
    setSavedSelection({ text: selectedText, offset });

    if (flashcardMode) {
      addSelectedTextToFlashcards(selectedText);
    } else {
      // Add selected text to notes (append to existing)
      if (notesEditMode === "cards") {
        addSelectedTextToCardNotes(selectedText);
      } else {
        setNoteText((prev) =>
          prev ? `${prev}\n\n\n${selectedText}` : selectedText,
        );
      }

      // Auto-scroll to bottom after adding new note
      setTimeout(() => {
        if (notesEditMode === "textarea" && notesTextareaRef.current) {
          notesTextareaRef.current.scrollTop =
            notesTextareaRef.current.scrollHeight;
        } else {
          const container = notesContainerRef.current;
          if (container) {
            container.scrollTop = container.scrollHeight;
          }
        }
      }, 0);
    }

    const startOffset = offset;
    const endOffset = startOffset + selectedText.length;

    const overlappingHighlights = highlights.filter(
      (h) =>
        (startOffset >= h.startOffset && startOffset < h.endOffset) ||
        (endOffset > h.startOffset && endOffset <= h.endOffset) ||
        (startOffset <= h.startOffset && endOffset >= h.endOffset),
    );

    const filteredHighlights = highlights.filter(
      (h) => !overlappingHighlights.includes(h),
    );

    const newHighlight = {
      id: generateId(),
      text: selectedText,
      startOffset,
      endOffset,
      timestamp: new Date().toISOString(),
    };

    const updatedHighlights = [...filteredHighlights, newHighlight];
    setHighlights(updatedHighlights);

    selection.removeAllRanges();
  }, [
    addSelectedTextToCardNotes,
    addSelectedTextToFlashcards,
    article,
    flashcardMode,
    highlightMode,
    highlights,
    notesEditMode,
  ]);

  const handleHighlightClick = React.useCallback((highlightId) => {
    if (!article || (!highlightMode && !flashcardMode)) return;

    setHighlights((prev) => prev.filter((h) => h.id !== highlightId));
  }, [article, flashcardMode, highlightMode]);

  const loadBookmark = React.useCallback(async (mode = "lesson") => {
    if (!article) return;
    try {
      const response = await articlesAPI.getLessonItems(article._id, "bookmark");
      const bookmarks = response.data.articles || [];
      const modeBookmark = bookmarks.find(b => b.chapter === mode);
      if (modeBookmark) {
        const newBookmark = { offset: parseInt(modeBookmark.body), id: modeBookmark._id };
        setBookmark(newBookmark);
        return newBookmark;
      } else {
        setBookmark(null);
        return null;
      }
    } catch {
      setBookmark(null);
      return null;
    }
  }, [article]);

  const handleBookmarkClick = async () => {
    if (!article || loadingBookmark) return;

    const currentMode = flashcardMode ? "flashcard" : highlightMode ? "note" : "lesson";

    if (highlightMode && savedSelection) {
      // TAKE NOTES MODE: use saved selection to create bookmark
      const offset = savedSelection.offset;

      setLoadingBookmark(true);
      try {
        const existingRes = await articlesAPI.getLessonItems(article._id, "bookmark");
        const existingBm = (existingRes.data.articles || []).find(b => b.chapter === currentMode);
        if (existingBm) {
          await articlesAPI.deleteArticle(existingBm._id);
        }
        const response = await articlesAPI.createArticle({
          contentType: "bookmark",
          body: offset.toString(),
          chapter: currentMode,
          lessonId: article._id,
          title: folderFromUrl || "",
        });
        setBookmark({ offset, id: response.data._id });

        const startOffset = offset;
        const endOffset = startOffset + savedSelection.text.length;
        setHighlights((prev) =>
          prev.filter(
            (h) =>
              !(
                h.startOffset === startOffset &&
                h.endOffset === endOffset
              ),
          ),
        );
        
        // Remove the note that was added
        if (notesEditMode === "cards") {
          setCardNotes((prev) => {
            const nextNotes = prev.filter(
              (note, idx) =>
                idx !== prev.length - 1 || note !== savedSelection.text,
            );
            return nextNotes.length > 0 ? nextNotes : [""];
          });
        } else {
          setNoteText(prev => {
            const parts = prev.split("\n\n\n");
            if (parts[parts.length - 1] === savedSelection.text) {
              parts.pop();
            }
            return parts.join("\n\n\n");
          });
        }
        
        setSavedSelection(null);
        addToast("Bookmark created", "success-info");
      } catch {
        addToast("Failed to create bookmark", "error-warning");
      } finally {
        setLoadingBookmark(false);
      }
    } else {
      // Navigate to bookmark or create new one
      const selection = window.getSelection();
      const selectedText = selection.toString().trim();
      
      if (selectedText) {
        const range = selection.getRangeAt(0);
        const lessonBody = bodyRef.current;
        
        if (lessonBody?.contains(range.commonAncestorContainer)) {
          const preSelectionRange = range.cloneRange();
          preSelectionRange.selectNodeContents(lessonBody);
          preSelectionRange.setEnd(range.startContainer, range.startOffset);
          const offset = preSelectionRange.toString().length;

          setLoadingBookmark(true);
          try {
            // Delete existing bookmark for current mode (fetch fresh from API)
            const existingRes = await articlesAPI.getLessonItems(article._id, "bookmark");
            const existingBm = (existingRes.data.articles || []).find(b => b.chapter === currentMode);
            if (existingBm) {
              await articlesAPI.deleteArticle(existingBm._id);
            }
            const response = await articlesAPI.createArticle({
              contentType: "bookmark",
              body: offset.toString(),
              chapter: currentMode,
              lessonId: article._id,
              title: folderFromUrl || "",
            });
            setBookmark({ offset, id: response.data._id });
            addToast("Bookmark created", "success-info");
          } catch {
            addToast("Failed to create bookmark", "error-warning");
          } finally {
            setLoadingBookmark(false);
          }
        }
      } else {
        // Navigate to bookmark
        if (bookmark) {
          const bookmarkEl = document.querySelector(`[data-bookmark-offset="${bookmark.offset}"]`);
          if (bookmarkEl) {
            bookmarkEl.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }
        } else {
          addToast("Select text to add a bookmark", "success-info");
        }
      }
    }
  };

  const handleDeleteBookmark = async (bookmarkId) => {
    if (!article || loadingBookmark) return;
    setLoadingBookmark(true);
    try {
      await articlesAPI.deleteArticle(bookmarkId);
      setBookmark(null);
      addToast("Bookmark deleted", "success-info");
    } catch {
      addToast("Failed to delete bookmark", "error-warning");
    } finally {
      setLoadingBookmark(false);
    }
  };

  const bookmarkIconProps = bookmark
    ? { color: "#3b82f6", fill: "#3b82f6" }
    : { fill: "none" };

  const findBoundaryContextMatches = React.useCallback(
    (text, beforeContext, afterContext, predictedOffset) => {
      const matches = [];
      const beforeLength = beforeContext.length;
      const afterLength = afterContext.length;
      const minOffset = beforeLength;
      const maxOffset = text.length - afterLength;

      for (let offset = minOffset; offset <= maxOffset; offset += 1) {
        const beforeMatches =
          !beforeLength ||
          text.slice(offset - beforeLength, offset) === beforeContext;
        const afterMatches =
          !afterLength || text.slice(offset, offset + afterLength) === afterContext;

        if (beforeMatches && afterMatches) {
          matches.push(offset);
        }
      }

      if (matches.length === 0) {
        return null;
      }

      return matches.reduce((bestMatch, currentMatch) => {
        const currentDistance = Math.abs(currentMatch - predictedOffset);
        const bestDistance = Math.abs(bestMatch - predictedOffset);
        return currentDistance < bestDistance ? currentMatch : bestMatch;
      }, matches[0]);
    },
    [],
  );

  const remapBookmarkOffset = React.useCallback(
    (previousText, nextText, originalOffset) => {
      const oldText = String(previousText || "");
      const newText = String(nextText || "");
      const safeOffset = Math.max(
        0,
        Math.min(Number.parseInt(originalOffset, 10) || 0, oldText.length),
      );

      if (oldText === newText) {
        return safeOffset;
      }

      let commonPrefixLength = 0;
      while (
        commonPrefixLength < oldText.length &&
        commonPrefixLength < newText.length &&
        oldText[commonPrefixLength] === newText[commonPrefixLength]
      ) {
        commonPrefixLength += 1;
      }

      let commonSuffixLength = 0;
      while (
        commonSuffixLength < oldText.length - commonPrefixLength &&
        commonSuffixLength < newText.length - commonPrefixLength &&
        oldText[oldText.length - 1 - commonSuffixLength] ===
          newText[newText.length - 1 - commonSuffixLength]
      ) {
        commonSuffixLength += 1;
      }

      const oldChangedEnd = oldText.length - commonSuffixLength;
      const newChangedEnd = newText.length - commonSuffixLength;
      const lengthDelta = newText.length - oldText.length;

      if (safeOffset <= commonPrefixLength) {
        return safeOffset;
      }

      if (safeOffset >= oldChangedEnd) {
        return Math.max(0, Math.min(safeOffset + lengthDelta, newText.length));
      }

      const predictedOffset = Math.max(
        commonPrefixLength,
        Math.min(commonPrefixLength + (safeOffset - commonPrefixLength), newChangedEnd),
      );

      const contextSizes = [32, 24, 16, 8];

      for (const contextSize of contextSizes) {
        const beforeContext = oldText.slice(
          Math.max(0, safeOffset - contextSize),
          safeOffset,
        );
        const afterContext = oldText.slice(
          safeOffset,
          Math.min(oldText.length, safeOffset + contextSize),
        );

        const matchedOffset = findBoundaryContextMatches(
          newText,
          beforeContext,
          afterContext,
          predictedOffset,
        );

        if (matchedOffset !== null) {
          return matchedOffset;
        }
      }

      return Math.max(0, Math.min(safeOffset + lengthDelta, newText.length));
    },
    [findBoundaryContextMatches],
  );

  const handleEditorialTagToggle = (tagSlug) => {
    setEditedArticle((prev) => {
      const currentTags = Array.isArray(prev.tags) ? prev.tags : [];

      if (currentTags.includes(tagSlug)) {
        return {
          ...prev,
          tags: currentTags.filter((tag) => tag !== tagSlug),
        };
      }

      if (currentTags.length >= EDITORIAL_LESSON_TAG_LIMIT) {
        addToast(
          `Select up to ${EDITORIAL_LESSON_TAG_LIMIT} tags`,
          "error-warning",
        );
        return prev;
      }

      return {
        ...prev,
        tags: [...currentTags, tagSlug],
      };
    });
  };

  const handleSaveEdit = async () => {
    if (!editedArticle.chapter.trim()) {
      addToast("Lesson title is required", "error-warning");
      return;
    }

    // Check if body is empty
    if (!editedArticle.body.trim() || editedArticle.body.trim() === " ") {
      addToast("Add content to save", "error-warning");
      return;
    }

    try {
      const updateResponse = await articlesAPI.updateArticle(article._id, {
        title: article.title,
        body: editedArticle.body,
        chapter: editedArticle.chapter,
        author: editedArticle.author,
        ...(showEditorialTagEditor ? { tags: editedArticle.tags } : {}),
      });

      const savedArticle = updateResponse?.data?._id
        ? updateResponse.data
        : { ...article, ...editedArticle };

      const previousBody = article.body;
      const nextBody = savedArticle.body;
      const remappedBookmarks = new Map();

      if (previousBody !== nextBody) {
        try {
          const bookmarkResponse = await articlesAPI.getLessonItems(
            article._id,
            "bookmark",
          );
          const lessonBookmarks = bookmarkResponse.data.articles || [];

          await Promise.all(
            lessonBookmarks.map(async (bookmarkArticle) => {
              const currentOffset = Number.parseInt(bookmarkArticle.body, 10);
              if (Number.isNaN(currentOffset)) {
                return;
              }

              const nextOffset = remapBookmarkOffset(
                previousBody,
                nextBody,
                currentOffset,
              );

              if (nextOffset === currentOffset) {
                remappedBookmarks.set(bookmarkArticle._id, currentOffset);
                return;
              }

              await articlesAPI.updateArticle(bookmarkArticle._id, {
                body: nextOffset.toString(),
              });
              remappedBookmarks.set(bookmarkArticle._id, nextOffset);
            }),
          );
        } catch {
          addToast(
            "Lesson updated, but bookmark position could not be refreshed",
            "error-warning",
          );
        }
      }

      syncLessonCaches(savedArticle);
      setArticle(savedArticle);
      setEditedArticle({
        body: savedArticle.body,
        chapter: savedArticle.chapter || "",
        author: savedArticle.author || "",
        tags: getArticleTags(savedArticle),
      });
      setBookmark((currentBookmark) => {
        if (!currentBookmark) {
          return currentBookmark;
        }

        const nextOffset = remappedBookmarks.get(currentBookmark.id);
        if (nextOffset === undefined) {
          return currentBookmark;
        }

        return { ...currentBookmark, offset: nextOffset };
      });
      // Notes/bookmarks stay separate from the lesson body update
      setEditMode(false);
      setHasUnsavedChanges(false);
      lessonJustCreatedRef.current = false; // Reset after saving
      addToast("Lesson updated", "success-info");
    } catch {
      addToast("Failed to update lesson", "error-warning");
    }
  };

  const handleCancelEdit = React.useCallback(async () => {
    if (!article) return;

    if (hasUnsavedChanges) {
      setShowCancelEditPrompt(true);
      return;
    }

    // If original body is empty or just a space, delete the lesson
    if (!article.body.trim() || article.body === " ") {
      try {
        await articlesAPI.deleteArticle(article._id);
        removeLessonFromCaches(article._id);
        invalidateLessonListCaches();
        addToast("No lesson created", "success-info");
        navigate(returnPath);
        return;
      } catch {
        addToast("Failed to delete empty lesson", "error-warning");
        return;
      }
    }

    // If edited body is empty, delete the lesson
    if (!editedArticle.body.trim() || editedArticle.body === " ") {
      try {
        await articlesAPI.deleteArticle(article._id);
        removeLessonFromCaches(article._id);
        invalidateLessonListCaches();
        addToast("No lesson created", "success-info");
        navigate(returnPath);
        return;
      } catch {
        addToast("Failed to delete empty lesson", "error-warning");
        return;
      }
    }

    setEditMode(false);
    setHasUnsavedChanges(false);
    setEditedArticle({
      body: article.body,
      chapter: article.chapter || "",
      author: article.author || "",
      tags: getArticleTags(article),
    });
  }, [
    article,
    editedArticle.body,
    hasUnsavedChanges,
    returnPath,
    addToast,
    navigate,
    removeLessonFromCaches,
    invalidateLessonListCaches,
  ]);

  const handleDelete = async () => {
    setDeletingLesson(true);
    try {
      await articlesAPI.deleteArticle(article._id);
      removeLessonFromCaches(article._id);
      invalidateLessonListCaches();
      addToast("Lesson deleted", "success-info");
      navigate(`/study/lessons?folder=${encodeURIComponent(folderFromUrl)}`);
    } catch {
      addToast("Failed to delete lesson", "error-warning");
    } finally {
      setDeletingLesson(false);
    }
  };

  const handleCreateLesson = async () => {
    setDeleteStep(0);
    if (highlightMode) {
      handleStopTakeNotes();
    }
    try {
      const response = await articlesAPI.createArticle({
        title: folderFromUrl,
        author: "",
        chapter: "",
        body: " ",
        contentType: "lesson",
      });
      const newLessonId = response.data._id;
      if (response.data?._id) {
        articleCacheRef.current.set(response.data._id, response.data);
      }
      invalidateLessonListCaches();
      lessonJustCreatedRef.current = true;
      // Force edit mode immediately to avoid state race on URL param handling.
      setEditMode(true);
      setHasUnsavedChanges(false);
      setEditedArticle({
        body: " ",
        chapter: "",
        author: "",
        tags: [],
      });
      const params = new URLSearchParams();
      params.set("folder", folderFromUrl || "");
      params.set("edit", "true");
      params.set(
        "returnTo",
        sanitizeReturnPath(`${location.pathname}${location.search}`),
      );
      navigate(`/view/lesson/${newLessonId}?${params.toString()}`);
    } catch {
      addToast("Failed to create lesson", "error-warning");
    }
  };

  const handleTakeNotes = async () => {
    setDeleteStep(0);
    // Open take-notes UI immediately; load notes in background.
    setHighlightMode(true);
    setHighlights([]);
    setSavedSelection(null);
    setLoadingNotesPanel(true);
    setLessonCopied(false);
    setNoteText("");
    setCardNotes([""]);
    setOriginalNoteText("");
    setOriginalCardNotes([""]);
    addToast("Take Notes mode", "success-info");

    // Load bookmark for notes mode
    await loadBookmark("note");

    // Add takeNotes parameter to URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set("takeNotes", "true");
    setSearchParams(newParams, { replace: true });

    try {
      // Load existing notes for this lesson
      const response = await articlesAPI.getLessonItems(article._id, "note");
      const existingNotes = (response.data.articles || []).sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );

      const loadedText =
        existingNotes.length > 0
          ? existingNotes.map((n) => n.body).join("\n\n\n")
          : "";

      setNoteText(loadedText);
      setCardNotes(loadedText ? loadedText.split("\n\n\n") : [""]);
      setOriginalNoteText(loadedText);
      setOriginalCardNotes(loadedText ? loadedText.split("\n\n\n") : [""]);
    } catch {
      addToast("Failed to load notes", "error-warning");
    } finally {
      setLoadingNotesPanel(false);
    }
  };

  const handleStopTakeNotes = async () => {
    // Prevent exit if saving or deleting
    if (savingNotes || deletingNotes) {
      return;
    }

    // Check if there are changes
    if (currentNotesText() !== originalNotesText()) {
      setShowSavePrompt(true);
    } else {
      setHighlightMode(false);
      setHighlights([]);
      setSavedSelection(null);
      
      // Remove takeNotes parameter from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("takeNotes");
      setSearchParams(newParams, { replace: true });
      
      // Reload bookmark for lesson mode
      await loadBookmark("lesson");
      
      addToast("Exited Take Notes", "success-info");
    }
  };

  const handleSaveNote = async () => {
    // Save current scroll position
    const container = notesContainerRef.current;
    const savedScroll = container ? container.scrollTop : 0;

    setSavingNotes(true);
    try {
      // Delete all existing notes for this lesson in bulk
      await articlesAPI.bulkDeleteNotes(article._id);

      const textToSave = currentNotesText();
      if (textToSave.trim()) {
        // Split noteText by triple newlines and create notes in bulk
        const paragraphs = textToSave
          .split("\n\n\n")
          .filter((p) => p.trim())
          .map((p) => p.trim());

        await articlesAPI.bulkCreateNotes({
          notes: paragraphs,
          lessonId: article._id,
          folderName: folderFromUrl,
          chapter: article.chapter,
          author: article.author,
        });

        addToast(`${paragraphs.length} note(s) saved`, "success-info");
      } else {
        addToast("All notes saved", "success-info");
      }

      setOriginalNoteText(textToSave);
      setOriginalCardNotes(textToSave ? textToSave.split("\n\n\n") : [""]);
    } catch {
      addToast("Failed to save note", "error-warning");
    } finally {
      setSavingNotes(false);
      // Restore scroll position after all textareas have resized
      setTimeout(() => {
        if (container) {
          container.scrollTop = savedScroll;
        }
      }, 50);
    }
  };

  const applyAiNotesDraft = useCallback(
    (notesDraft = []) => {
      const normalizedNotes = Array.isArray(notesDraft)
        ? notesDraft.map((note) => String(note || "").trim()).filter(Boolean)
        : [];

      if (normalizedNotes.length === 0) {
        throw new Error("AI returned no usable notes");
      }

      const nextText = normalizedNotes.join("\n\n\n");

      setNoteText((prev) => (prev.trim() ? `${prev}\n\n\n${nextText}` : nextText));
      setCardNotes((prev) => {
        const existingNotes =
          Array.isArray(prev) && !(prev.length === 1 && !String(prev[0] || "").trim())
            ? prev
            : [];

        return [...existingNotes, ...normalizedNotes];
      });
    },
    [setCardNotes, setNoteText],
  );

  const runAiNotesGeneration = useCallback(async () => {
    if (!article?._id) {
      addToast("Lesson not loaded yet", "error-warning");
      return;
    }

    setGeneratingAiNotes(true);
    try {
      const response = await articlesAPI.generateNotesDraft(
        article._id,
        "main_ideas_preserve_text",
      );
      applyAiNotesDraft(response.data?.notes || []);
      addToast("AI notes draft loaded locally", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.message || "Failed to generate AI notes",
        "error-warning",
      );
    } finally {
      setGeneratingAiNotes(false);
    }
  }, [addToast, applyAiNotesDraft, article?._id]);

  const handleGenerateAiNotes = useCallback(async () => {
    if (generatingAiNotes || savingNotes || loadingNotesPanel) {
      return;
    }

    await runAiNotesGeneration();
  }, [
    generatingAiNotes,
    loadingNotesPanel,
    runAiNotesGeneration,
    savingNotes,
  ]);

  const handleSaveIndividualNote = async (index) => {
    const note = notes[index];
    if (!note || !note.trim()) {
      addToast("Note is empty", "error-warning");
      return;
    }

    setSavingIndividualNote({ ...savingIndividualNote, [index]: true });
    try {
      await articlesAPI.bulkDeleteNotes(article._id);
      const validNotes = notes.filter((n) => n && n.trim());
      if (validNotes.length === 0) {
        addToast("No notes to save", "error-warning");
        return;
      }
      await articlesAPI.bulkCreateNotes({
        notes: validNotes,
        lessonId: article._id,
        folderName: folderFromUrl,
        chapter: article.chapter,
        author: article.author,
      });
      addToast("Note saved", "success-info");
    } catch {
      addToast("Failed to save note", "error-warning");
    } finally {
      setSavingIndividualNote({ ...savingIndividualNote, [index]: false });
      const savedText = currentNotesText();
      setOriginalNoteText(savedText);
      setOriginalCardNotes(savedText ? savedText.split("\n\n\n") : [""]);
    }
  };

  const handleDeleteIndividualNote = (index) => {
    setCardNotes((prev) => {
      const updatedNotes = prev.filter((_, i) => i !== index);
      return updatedNotes.length > 0 ? updatedNotes : [""];
    });
  };

  const handleConfirmDeleteAllNotes = () => {
    setNoteText("");
    setCardNotes([""]);
    setDeleteNotesStep(0);
    addToast(
      "Notes cleared locally. Press Save to apply.",
      "success-info",
    );
  };

  const handleUpdateNote = (index, value) => {
    setCardNotes((prev) => {
      const updatedNotes = [...prev];
      updatedNotes[index] = value;
      return updatedNotes;
    });
  };

  const handleConfirmDeleteAllFlashcards = () => {
    setFlashcards([{ question: "", answer: "" }]);
    setDeleteFlashcardsStep(0);
    addToast(
      "Flashcards cleared locally. Press Save to apply.",
      "success-info",
    );
  };



  const handleAddNote = () => {
    setCardNotes((prev) => [...prev, ""]);
    setTimeout(() => {
      const container = notesContainerRef.current;
      if (!container) return;

      // Focus the last textarea and ensure it's fully visible inside the notes panel.
      const textareas = container.querySelectorAll("textarea");
      const lastTextarea = textareas[textareas.length - 1];
      if (!lastTextarea) return;

      try {
        lastTextarea.focus({ preventScroll: true });
      } catch {
        lastTextarea.focus();
      }

      const containerRect = container.getBoundingClientRect();
      const textareaRect = lastTextarea.getBoundingClientRect();
      const bottomPadding = 20;

      if (textareaRect.bottom > containerRect.bottom - bottomPadding) {
        container.scrollTop +=
          textareaRect.bottom - (containerRect.bottom - bottomPadding);
      } else if (textareaRect.top < containerRect.top) {
        container.scrollTop -= containerRect.top - textareaRect.top + 8;
      }
    }, 0);
  };

  const ensureNotesTextareaVisible = React.useCallback((textareaEl) => {
    const container = notesContainerRef.current;
    if (!container || !textareaEl) return;

    const containerRect = container.getBoundingClientRect();
    const textareaRect = textareaEl.getBoundingClientRect();
    const bottomPadding = 28;
    const topPadding = 8;

    if (textareaRect.bottom > containerRect.bottom - bottomPadding) {
      container.scrollTop +=
        textareaRect.bottom - (containerRect.bottom - bottomPadding);
    } else if (textareaRect.top < containerRect.top + topPadding) {
      container.scrollTop -= containerRect.top + topPadding - textareaRect.top;
    }
  }, []);

  const handleViewNotes = async () => {
    if (loadingNotesPanel) return;

    const textToView = currentNotesText();
    if (!textToView.trim()) {
      addToast("No notes to view", "error-warning");
      return;
    }

    // Check if there are unsaved changes
    if (textToView !== originalNoteText) {
      setShouldNavigateToNotes(true);
      setShowSavePrompt(true);
      return;
    }

    // Navigate directly if no changes
    navigate(
      `/study/notes?folder=${encodeURIComponent(folderFromUrl)}&type=note&lessonId=${article._id}`,
    );
  };

  const handleCreateFlashcards = async () => {
    setDeleteStep(0);
    setFlashcardMode(true);
    setLoadingFlashcardsPanel(true);
    
    // Add flashcards parameter to URL
    const newParams = new URLSearchParams(searchParams);
    newParams.set("flashcards", "true");
    setSearchParams(newParams, { replace: true });
    
    try {
      // Load existing flashcards for this lesson
      const response = await articlesAPI.getLessonItems(
        article._id,
        "flashcard",
      );
      const existingFlashcards = (response.data.articles || []).sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );

      const loadedFlashcards =
        existingFlashcards.length > 0
          ? existingFlashcards.map((fc) => ({
              question: fc.chapter,
              answer: fc.body,
            }))
          : [{ question: "", answer: "" }];

      setFlashcards(loadedFlashcards);
      setOriginalFlashcards(JSON.parse(JSON.stringify(loadedFlashcards)));
      
      // Load bookmark for flashcard mode AFTER setting flashcards
      await loadBookmark("flashcard");
      
      addToast("Create Flashcards mode", "success-info");
    } catch {
      addToast("Failed to load flashcards", "error-warning");
      setFlashcards([{ question: "", answer: "" }]);
      setOriginalFlashcards([{ question: "", answer: "" }]);
    } finally {
      setLoadingFlashcardsPanel(false);
    }
  };

  const handleCopyFromNotes = async () => {
    try {
      const response = await articlesAPI.getLessonItems(article._id, "note");
      const existingNotes = (response.data.articles || []).sort(
        (a, b) => (a.order || 0) - (b.order || 0),
      );

      if (existingNotes.length === 0) {
        addToast("No notes to copy", "error-warning");
        return;
      }

      const newFlashcards = existingNotes.map((note) => ({
        question: "Question",
        answer: note.body,
      }));

      setFlashcards([...newFlashcards, ...flashcards]);
      addToast(`Copied ${newFlashcards.length} note(s)`, "success-info");
    } catch {
      addToast("Failed to copy notes", "error-warning");
    }
  };

  const resetFlashcardsSession = React.useCallback(() => {
    setFlashcardMode(false);
    setFlashcards([{ question: "", answer: "" }]);
    setFullscreenFlashcards(false);
    setHighlights([]);
    setSavedSelection(null);
  }, []);

  const handleStopFlashcards = async () => {
    // Prevent exit if saving or deleting
    if (savingFlashcards || deletingFlashcards) {
      return;
    }

    // Check if there are unsaved changes
    const hasChanges =
      JSON.stringify(flashcards) !== JSON.stringify(originalFlashcards);

    if (hasChanges) {
      setShowSaveFlashcardsPrompt(true);
    } else {
      resetFlashcardsSession();
      
      // Remove flashcards parameter from URL
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("flashcards");
      setSearchParams(newParams, { replace: true });
      
      // Reload bookmark for lesson mode
      await loadBookmark("lesson");
      addToast("Exited Flashcards", "success-info");
    }
  };

  const handleExitFullscreenFlashcards = () => {
    setFullscreenFlashcards(false);
  };

  const handleSaveIndividualFlashcard = async (index) => {
    const fc = flashcards[index];
    if (!fc.question.trim() || !fc.answer.trim()) {
      addToast("Question and answer required", "error-warning");
      return;
    }

    setSavingIndividualFC({ ...savingIndividualFC, [index]: true });
    try {
      await articlesAPI.bulkDeleteFlashcards(article._id);
      const validFlashcards = flashcards.filter(
        (fc) => fc.question.trim() && fc.answer.trim(),
      );
      await articlesAPI.bulkCreateFlashcards({
        flashcards: validFlashcards,
        lessonId: article._id,
        folderName: folderFromUrl,
        chapter: article.chapter,
        author: article.author,
      });
      setOriginalFlashcards(JSON.parse(JSON.stringify(flashcards)));
      addToast("Flashcard saved", "success-info");
    } catch {
      addToast("Failed to save flashcard", "error-warning");
    } finally {
      setSavingIndividualFC({ ...savingIndividualFC, [index]: false });
    }
  };

  const handleSaveFlashcards = async () => {
    // Check if there are any changes
    const hasChanges =
      JSON.stringify(flashcards) !== JSON.stringify(originalFlashcards);
    if (!hasChanges) {
      addToast("No changes to save", "success-info");
      return;
    }

    setSavingFlashcards(true);
    try {
      // Delete all existing flashcards for this lesson in bulk
      await articlesAPI.bulkDeleteFlashcards(article._id);

      // Filter out empty flashcards
      const validFlashcards = flashcards.filter(
        (fc) => fc.question.trim() && fc.answer.trim(),
      );

      if (validFlashcards.length > 0) {
        await articlesAPI.bulkCreateFlashcards({
          flashcards: validFlashcards,
          lessonId: article._id,
          folderName: folderFromUrl,
          chapter: article.chapter,
          author: article.author,
        });

        addToast(
          `${validFlashcards.length} flashcard(s) saved`,
          "success-info",
        );
      } else {
        addToast("Flashcards cleared", "success-info");
      }

      setOriginalFlashcards(JSON.parse(JSON.stringify(flashcards)));
    } catch {
      addToast("Failed to save flashcards", "error-warning");
    } finally {
      setSavingFlashcards(false);
    }
  };

  const addFlashcard = () => {
    setFlashcards((prev) => [...prev, { question: "", answer: "" }]);
    setTimeout(() => {
      const container = flashcardsContainerRef.current;
      const lastQuestion =
        flashcardQuestionRefs.current[flashcardQuestionRefs.current.length - 1];

      if (!container || !lastQuestion) return;

      try {
        lastQuestion.focus({ preventScroll: true });
      } catch {
        lastQuestion.focus();
      }

      const containerRect = container.getBoundingClientRect();
      const textareaRect = lastQuestion.getBoundingClientRect();
      const bottomPadding = 28;

      if (textareaRect.bottom > containerRect.bottom - bottomPadding) {
        container.scrollTop +=
          textareaRect.bottom - (containerRect.bottom - bottomPadding);
      } else {
        container.scrollTop = container.scrollHeight - container.clientHeight;
      }
    }, 0);
  };

  const removeFlashcard = (index) => {
    setFlashcards(flashcards.filter((_, i) => i !== index));
  };

  const updateFlashcard = (index, field, value) => {
    const updated = [...flashcards];
    updated[index][field] = value;
    setFlashcards(updated);
  };



  const prefetchArticle = React.useCallback(async (articleId) => {
    if (!articleId || articleCacheRef.current.has(articleId)) return;
    try {
      const res = await articlesAPI.getArticleById(articleId);
      if (res?.data?.article) {
        articleCacheRef.current.set(articleId, res.data.article);
      }
    } catch {
      // Prefetch is best-effort only.
    }
  }, []);

  const handleShareToCommunity = React.useCallback(() => {
    if (!article?._id) return;

    const currentPath = `${location.pathname}${location.search}`;
    navigate(
      `/communities?shareLesson=${article._id}&returnTo=${encodeURIComponent(currentPath)}`,
    );
  }, [article?._id, location.pathname, location.search, navigate]);

  const handleTogglePublic = async () => {
    if (!article?._id || togglingPublic) return;

    setTogglingPublic(true);
    try {
      const response = await articlesAPI.toggleArticlePublic(article._id);
      const nextIsPublic = Boolean(response.data?.article?.isPublic);
      setArticle((prev) =>
        prev
          ? {
              ...prev,
              isPublic: nextIsPublic,
            }
          : prev,
      );
      addToast(
        nextIsPublic ? "Lesson is now public" : "Lesson is now private",
        "success-info",
      );
    } catch (error) {
      addToast(
        error.response?.data?.message || "Failed to update lesson visibility",
        "error-warning",
      );
    } finally {
      setTogglingPublic(false);
    }
  };

  useEffect(() => {
    if (article) {
      setEditedArticle({
        body: article.body,
        chapter: article.chapter || "",
        author: article.author || "",
        tags: getArticleTags(article),
      });
    }
  }, [article]);

  useEffect(() => {
    if (!article) return;
    const changed =
      editedArticle.body !== article.body ||
      editedArticle.chapter !== (article.chapter || "") ||
      editedArticle.author !== (article.author || "") ||
      !areTagListsEqual(editedArticle.tags || [], getArticleTags(article));
    setHasUnsavedChanges(changed);
  }, [editedArticle, article]);



  // Convert text offset (visible chars only) to HTML offset (including tags)
  const textOffsetToHtmlOffset = (html, textOffset) => {
    let textCount = 0;
    let i = 0;
    while (i < html.length && textCount < textOffset) {
      if (html[i] === '<') {
        while (i < html.length && html[i] !== '>') i++;
        i++; // skip '>'
      } else {
        textCount++;
        i++;
      }
    }
    return i;
  };

  // parseMarkdown escapes visible entities in every mode, so &amp; / &lt; / &gt;
  // must always count as a single visible character when we place markers.
  const textOffsetToEscapedHtmlOffset = (html, textOffset) => {
    let textCount = 0;
    let i = 0;

    while (i < html.length && textCount < textOffset) {
      if (html[i] === "<") {
        while (i < html.length && html[i] !== ">") i++;
        i++;
        continue;
      }

      if (html[i] === "&") {
        const semicolonIndex = html.indexOf(";", i);
        if (semicolonIndex !== -1) {
          i = semicolonIndex + 1;
          textCount++;
          continue;
        }
      }

      textCount++;
      i++;
    }

    return i;
  };

  // Build lesson HTML with bookmark marker injection
  const lessonBodyHtml = useMemo(() => {
    if (!article) return "";
    const text = parseMarkdown(article.body);
    if (highlights.length === 0 && !bookmark) return text;
    const offsetToHtmlOffset = textOffsetToEscapedHtmlOffset;
    let result = "";
    let lastIndex = 0;

    const sortedHighlights = [...highlights].sort(
      (a, b) => a.startOffset - b.startOffset,
    );

    sortedHighlights.forEach((highlight) => {
      const start = Math.max(
        0,
        Math.min(offsetToHtmlOffset(text, highlight.startOffset), text.length),
      );
      const end = Math.max(
        start,
        Math.min(offsetToHtmlOffset(text, highlight.endOffset), text.length),
      );

      if (bookmark) {
        const bmHtmlOffset = offsetToHtmlOffset(text, bookmark.offset);
        if (bmHtmlOffset >= lastIndex && bmHtmlOffset < start) {
          result += text.slice(lastIndex, bmHtmlOffset);
          result += `<span class="bookmark-icon" data-bookmark-offset="${bookmark.offset}" data-bookmark-id="${bookmark.id}"></span>`;
          result += text.slice(bmHtmlOffset, start);
          lastIndex = start;
        } else {
          result += text.slice(lastIndex, start);
          lastIndex = start;
        }
      } else {
        result += text.slice(lastIndex, start);
        lastIndex = start;
      }

      result += `<mark class="highlight-active" data-highlight-id="${highlight.id}">${text.slice(start, end)}</mark>`;
      lastIndex = end;
    });

    if (bookmark) {
      const bmHtmlOffset = offsetToHtmlOffset(text, bookmark.offset);
      if (bmHtmlOffset >= lastIndex) {
        result += text.slice(lastIndex, bmHtmlOffset);
        result += `<span class="bookmark-icon" data-bookmark-offset="${bookmark.offset}" data-bookmark-id="${bookmark.id}"></span>`;
        result += text.slice(bmHtmlOffset);
      } else {
        result += text.slice(lastIndex);
      }
    } else {
      result += text.slice(lastIndex);
    }
    return result;
  }, [article, highlights, highlightMode, bookmark, flashcardMode]);

  // Event delegation for bookmark/highlight clicks on lesson body
  const handleLessonBodyClick = useCallback((e) => {
    const bookmarkEl = e.target.closest(".bookmark-icon");
    if (bookmarkEl) {
      const bookmarkId = bookmarkEl.getAttribute("data-bookmark-id");
      if (bookmarkId) handleDeleteBookmark(bookmarkId);
      return;
    }
    const markEl = e.target.closest("mark.highlight-active");
    if (markEl) {
      const highlightId = markEl.getAttribute("data-highlight-id");
      if (highlightId) handleHighlightClick(highlightId);
    }
  }, [handleDeleteBookmark, handleHighlightClick]);

  useEffect(() => {
    if (!highlightMode && !flashcardMode) return;

    document.addEventListener("mouseup", handleTextSelection);
    return () => {
      document.removeEventListener("mouseup", handleTextSelection);
    };
  }, [flashcardMode, highlightMode, handleTextSelection]);

  // Load bookmark for lesson mode after article is set
  useEffect(() => {
    if (article && !editMode && !highlightMode && !flashcardMode && !openFlashcardsFromUrl && !flashcardsFromUrl) {
      loadBookmark("lesson");
    }
  }, [article, editMode, highlightMode, flashcardMode, openFlashcardsFromUrl, flashcardsFromUrl, loadBookmark]);

  useEffect(() => {
    const loadArticles = async () => {
      try {
        const cachedLesson = articleCacheRef.current.get(id);
        const lessonRes = cachedLesson
          ? { data: { article: cachedLesson } }
          : await articlesAPI.getArticleById(id);
        const currentLesson = lessonRes?.data?.article;

        if (!currentLesson) {
          setLoading(false);
          return;
        }

        articleCacheRef.current.set(currentLesson._id, currentLesson);

        // Render the current lesson immediately; lesson list can arrive after.
        setArticle(currentLesson);
        setLoading(false);

        let lessons = [];
        try {
          const cacheKey = folderFromUrl || "__ALL__";
          if (lessonsCacheRef.current.has(cacheKey)) {
            lessons = lessonsCacheRef.current.get(cacheKey) || [];
          } else if (folderFromUrl) {
            const browserRes =
              await articlesAPI.getLessonsBrowser(folderFromUrl);
            lessons = browserRes.data.lessons || [];
            lessonsCacheRef.current.set(cacheKey, lessons);
          } else {
            const lessonsRes = await articlesAPI.getMyArticles("lesson", true);
            lessons = (lessonsRes.data.articles || []).filter(
              (a) => a.contentType === "lesson",
            );
            lessonsCacheRef.current.set(cacheKey, lessons);
          }
        } catch {
          lessons = [];
        }

        // Find current article index
        let currentIdx = lessons.findIndex((a) => a._id === id);
        if (currentIdx === -1 && currentLesson) {
          lessons = [...lessons, currentLesson];
          currentIdx = lessons.length - 1;
        }
        setArticles(lessons);

        if (currentIdx !== -1 && currentLesson) {
          setCurrentIndex(currentIdx);
          const prevIdx = currentIdx > 0 ? currentIdx - 1 : lessons.length - 1;
          const nextIdx = currentIdx < lessons.length - 1 ? currentIdx + 1 : 0;
          prefetchArticle(lessons[prevIdx]?._id);
          prefetchArticle(lessons[nextIdx]?._id);

          // Load bookmark for lesson mode on initial load - wait a bit for article to be fully set
          setTimeout(() => {
            loadBookmark("lesson");
          }, 100);

          // If lesson is empty and not in edit mode and wasn't just created, delete it (refresh case)
          if (
            !editMode &&
            !lessonJustCreatedRef.current &&
            (!currentLesson.body.trim() || currentLesson.body === " ")
          ) {
            if (!emptyLessonDeletedRef.current) {
              emptyLessonDeletedRef.current = true;
              try {
                await articlesAPI.deleteArticle(currentLesson._id);
                removeLessonFromCaches(currentLesson._id);
                invalidateLessonListCaches();
                addToast("No lesson created", "success-info");
                navigate(returnPath);
                return;
              } catch {
                addToast("Failed to delete empty lesson", "error-warning");
              }
            }
          }

          // Set edit mode if edit=true in URL
          const isDifferentLesson =
            lastLoadedLessonIdRef.current !== currentLesson._id;
          lastLoadedLessonIdRef.current = currentLesson._id;

          if (editFromUrl) {
            setEditMode(true);
            // Remove edit parameter from URL to prevent persistence on back/refresh
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("edit");
            setSearchParams(newParams, { replace: true });
          } else if (isDifferentLesson) {
            // Reset only when navigating to another lesson without edit=true.
            // Do not reset on the same lesson right after cleaning URL params.
            setEditMode(false);
          }

          // Show toast if new lesson in edit mode (only once)
          if (
            editFromUrl &&
            !toastShownRef.current &&
            (!currentLesson.body.trim() || currentLesson.body === " ")
          ) {
            addToast("Lesson created", "success-info");
            toastShownRef.current = true;
          }
        }
      } catch {
        if (!errorShownRef.current) {
          addToast("Failed to load lesson", "error-warning");
          errorShownRef.current = true;
        }
        setLoading(false);
      }
    };
    loadArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, folderFromUrl, addToast, editFromUrl, prefetchArticle, returnPath]);

  // Reset error flag when id or folder changes
  useEffect(() => {
    errorShownRef.current = false;
  }, [id, folderFromUrl]);

  useEffect(() => {
    if (!openTakeNotesFromUrl || !takeNotesFromUrl) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("takeNotes");
    setSearchParams(newParams, { replace: true });
  }, [openTakeNotesFromUrl, takeNotesFromUrl, searchParams, setSearchParams]);

  // Auto-open Take Notes only for the active editing flag.
  // openTakeNotes keeps the lesson-view context when returning from Study Notes.
  useEffect(() => {
    if (
      takeNotesFromUrl &&
      !openTakeNotesFromUrl &&
      article &&
      !editMode &&
      !highlightMode &&
      !flashcardMode &&
      !takeNotesTriggeredRef.current
    ) {
      takeNotesTriggeredRef.current = true;
      handleTakeNotes();
    }
  }, [openTakeNotesFromUrl, takeNotesFromUrl, article, editMode, highlightMode, flashcardMode]);

  // Auto-open Flashcards if openFlashcards=true or flashcards=true in URL
  useEffect(() => {
    if (
      (openFlashcardsFromUrl || flashcardsFromUrl) &&
      article &&
      !editMode &&
      !highlightMode &&
      !flashcardMode &&
      !takeNotesTriggeredRef.current
    ) {
      takeNotesTriggeredRef.current = true;
      handleCreateFlashcards();
      // Remove openFlashcards parameter from URL after opening
      if (openFlashcardsFromUrl) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("openFlashcards");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [openFlashcardsFromUrl, flashcardsFromUrl, article, editMode, highlightMode, flashcardMode]);

  useEffect(() => {
    if (notesEditMode !== "cards") return;
    setCardNotes(noteText ? noteText.split("\n\n\n") : [""]);
  }, [noteText, notesEditMode]);

  useEffect(() => {
    if (!editMode && !highlightMode && !flashcardMode) return;

    const handleBeforeUnload = (e) => {
      if (editMode && hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    const handlePopState = () => {
      if (editMode || highlightMode || flashcardMode) {
        // Block navigation completely
        window.history.pushState(null, "", window.location.href);
      }
    };

    // Add one history entry per blocked-session (not on every state change),
    // otherwise browser Back becomes very slow after many edits.
    if ((editMode || highlightMode || flashcardMode) && !navigationBlockStatePushedRef.current) {
      window.history.pushState(null, "", window.location.href);
      navigationBlockStatePushedRef.current = true;
    }

    const handleKeyDown = async (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (editMode) {
          await handleCancelEdit();
        } else if (highlightMode) {
          handleStopTakeNotes();
        } else if (flashcardMode) {
          if (fullscreenFlashcards) {
            setFullscreenFlashcards(false);
          } else {
            handleStopFlashcards();
          }
        }
      }
    };

    const handleClick = (e) => {
      // Allow navigation for buttons with data-allow-navigation
      if (e.target.closest('[data-allow-navigation="true"]')) {
        return;
      }

      // Check if clicking on navigation elements (breadcrumbs, buttons, etc)
      const isNavigation =
        e.target.closest(".breadcrumb-item") ||
        e.target.closest(".breadcrumb-home-icon") ||
        e.target.closest(
          '.study-button:not([title="Save"]):not([title="Save Flashcards"]):not([title="Copy from Notes"]):not([title="Cancel"]):not([title="View Notes"]):not([title="View Flashcards"]):not([title="Close"]):not([title="Clear Notes"]):not([title="Delete All Notes"]):not([title="Clear Flashcards"]):not([title="Fullscreen notes"]):not([title="Exit fullscreen notes"]):not([title="Fullscreen flashcards"]):not([title="Exit fullscreen flashcards"]):not([title="Add Flashcard"]):not([title="Add Note"]):not([title="Switch to textarea mode"]):not([title="Switch to cards mode"]):not([title="Copy Lesson to Notes"]):not([title="Save Notes"]):not([title="Bookmark"]):not(.save-notes-btn):not(.copy-to-notes-btn):not(.view-notes-btn):not(.view-flashcards-btn)',
        ) ||
        e.target.closest("a");

      if (isNavigation) {
        if (editMode) {
          e.preventDefault();
          e.stopPropagation();
          handleCancelEdit();
        } else if (highlightMode) {
          e.preventDefault();
          e.stopPropagation();
          handleStopTakeNotes();
        } else if (flashcardMode) {
          e.preventDefault();
          e.stopPropagation();
          if (fullscreenFlashcards) {
            setFullscreenFlashcards(false);
          } else {
            handleStopFlashcards();
          }
        }
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("click", handleClick, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("click", handleClick, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    editMode,
    hasUnsavedChanges,
    highlightMode,
    flashcardMode,
    fullscreenFlashcards,
    flashcards,
    originalFlashcards,
    handleCancelEdit,
    noteText,
    originalNoteText,
    cardNotes,
    originalCardNotes,
    notesEditMode,
    currentNotesText,
    originalNotesText,
  ]);

  useEffect(() => {
    if (!editMode && !highlightMode && !flashcardMode) {
      navigationBlockStatePushedRef.current = false;
    }
  }, [editMode, highlightMode, flashcardMode]);

  useEffect(() => {
    const shouldHideNav = editMode || highlightMode || flashcardMode;
    document.body.classList.toggle("view-lesson-hide-nav", shouldHideNav);
    return () => {
      document.body.classList.remove("view-lesson-hide-nav");
    };
  }, [editMode, highlightMode, flashcardMode]);

  const handlePrev = () => {
    if (editMode && hasUnsavedChanges) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        )
      ) {
        return;
      }
    }
    setDeleteStep(0);
    const prevIndex = currentIndex > 0 ? currentIndex - 1 : articles.length - 1;
    const prevArticle = articles[prevIndex];
    const cachedPrev = articleCacheRef.current.get(prevArticle._id);
    if (cachedPrev) {
      setArticle(cachedPrev);
      setCurrentIndex(prevIndex);
    }
    navigate(buildLessonPath(prevArticle._id));
  };

  const handleNext = () => {
    if (editMode && hasUnsavedChanges) {
      if (
        !window.confirm(
          "You have unsaved changes. Are you sure you want to leave?",
        )
      ) {
        return;
      }
    }
    setDeleteStep(0);
    const nextIndex = currentIndex < articles.length - 1 ? currentIndex + 1 : 0;
    const nextArticle = articles[nextIndex];
    const cachedNext = articleCacheRef.current.get(nextArticle._id);
    if (cachedNext) {
      setArticle(cachedNext);
      setCurrentIndex(nextIndex);
    }
    navigate(buildLessonPath(nextArticle._id));
  };

  const buildBreadcrumbs = () => {
    const breadcrumbs = [
      { label: "MySpace", path: "/myspace" },
      { label: "Classes", path: "/study/lessons" },
    ];

    if (folderFromUrl) {
      const parts = folderFromUrl.split(" / ");
      parts.forEach((part, index) => {
        const path = parts.slice(0, index + 1).join(" / ");
        breadcrumbs.push({
          label: part,
          path: `/study/lessons?folder=${encodeURIComponent(path)}`,
        });
      });
    }

    if (article) {
      breadcrumbs.push({
        label: article.chapter || "New Lesson",
        path:
          highlightMode || flashcardMode
            ? buildLessonPath(article._id)
            : null,
      });
    }

    if (highlightMode) {
      breadcrumbs.push({
        label: "Take Notes",
        path: null,
      });
    }

    if (flashcardMode) {
      breadcrumbs.push({
        label: "Flashcards",
        path: null,
      });
    }

    return breadcrumbs;
  };

  if (loading) {
    return (
      <StudyLayout breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}>
        <Spinner visible={true} />
      </StudyLayout>
    );
  }

  if (!article) {
    return (
      <StudyLayout breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}>
        <div className="viewlesson-not-found">
          <p>Lesson not found</p>
          <Button
            className="button-btn"
            data-allow-navigation="true"
            onClick={() => navigate(folderFromUrl ? `/study/lessons?folder=${encodeURIComponent(folderFromUrl)}` : "/myspace")}
          >
            Go back
          </Button>
        </div>
      </StudyLayout>
    );
  }

  return (
    <StudyLayout
      className={`viewlesson-page${editMode ? " viewlesson-page-editing" : ""}`}
      breadcrumbs={<Breadcrumbs path={buildBreadcrumbs()} />}
      buttons={
        <>
          <button
            className="study-button"
            onClick={handlePrev}
            disabled={
              articles.length <= 1 || editMode || highlightMode || flashcardMode
            }
            style={{
              minWidth: compactSidebar ? "40px" : "100px",
              width: compactSidebar ? "40px" : "auto",
              height: compactSidebar ? "40px" : "auto",
              borderRadius: compactSidebar ? "50%" : "8px",
              padding: compactSidebar ? "10px" : "8px 12px",
              transition:
                articles.length <= 1 ||
                editMode ||
                highlightMode ||
                flashcardMode
                  ? "none"
                  : "all 0.3s ease",
              marginRight: "-6px",
              visibility:
                articles.length <= 1 ||
                editMode ||
                highlightMode ||
                flashcardMode
                  ? "hidden"
                  : "visible",
            }}
            title="Previous"
          >
            {compactSidebar ? <ChevronLeft size={18} /> : "Previous"}
          </button>
          <button
            className="study-button"
            onClick={handleNext}
            disabled={
              articles.length <= 1 || editMode || highlightMode || flashcardMode
            }
            style={{
              minWidth: compactSidebar ? "40px" : "100px",
              width: compactSidebar ? "40px" : "auto",
              height: compactSidebar ? "40px" : "auto",
              borderRadius: compactSidebar ? "50%" : "8px",
              padding: compactSidebar ? "10px" : "8px 12px",
              transition:
                articles.length <= 1 ||
                editMode ||
                highlightMode ||
                flashcardMode
                  ? "none"
                  : "all 0.3s ease",
              marginRight: "35px",
              visibility:
                articles.length <= 1 ||
                editMode ||
                highlightMode ||
                flashcardMode
                  ? "hidden"
                  : "visible",
            }}
            title="Next"
          >
            {compactSidebar ? <ChevronRight size={18} /> : "Next"}
          </button>
          <button
            className="study-button"
            onClick={handleBookmarkClick}
            disabled={editMode || loadingBookmark || highlightMode || flashcardMode}
            style={{
              minWidth: "40px",
              width: "40px",
              height: "40px",
              borderRadius: "50%",
              padding: "10px",
              transition:
                editMode || highlightMode || flashcardMode
                  ? "none"
                  : "all 0.3s ease",
              marginRight: "-6px",
              visibility:
                editMode || highlightMode || flashcardMode
                  ? "hidden"
                  : "visible",
            }}
            title="Bookmark"
          >
            <Bookmark size={18} {...bookmarkIconProps} />
          </button>
          <button
            className="study-button"
            onClick={handleCreateLesson}
            style={{
              minWidth: compactSidebar ? "40px" : "100px",
              width: compactSidebar ? "40px" : "auto",
              height: compactSidebar ? "40px" : "auto",
              borderRadius: compactSidebar ? "50%" : "8px",
              padding: compactSidebar ? "10px" : "8px 12px",
              transition:
                editMode || highlightMode || flashcardMode
                  ? "none"
                  : "all 0.3s ease",
              marginRight: "-6px",
              visibility:
                editMode || highlightMode || flashcardMode
                  ? "hidden"
                  : "visible",
            }}
            disabled={editMode || highlightMode || flashcardMode}
            title="New Lesson"
          >
            {compactSidebar ? <Plus size={18} /> : "New Lesson"}
          </button>
          <div
            style={{
              position: "relative",
              minWidth: editMode
                ? compactSidebar
                  ? "128px"
                  : "136px"
                : compactSidebar
                  ? "40px"
                  : "100px",
              width: editMode
                ? compactSidebar
                  ? "128px"
                  : "136px"
                : "auto",
              height: "40px",
              marginRight: "14px",
            }}
          >
            {editMode ? (
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                {showPdfImport && (
                  <PdfImportPanel
                    currentText={editedArticle.body}
                    onImportText={(nextBody) =>
                      setEditedArticle((prev) => ({
                        ...prev,
                        body: nextBody,
                      }))
                    }
                    maxLength={lessonBodyLimit}
                    placement="viewlesson-top"
                    triggerVariant="icon"
                  />
                )}
                <button
                  className="study-button"
                  onClick={handleCancelEdit}
                  style={{
                    minWidth: "40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                  title="Cancel"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#dc3545"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <button
                  className="study-button"
                  onClick={handleSaveEdit}
                  style={{
                    minWidth: "40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                  title="Save"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#28a745"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                    <polyline points="17 21 17 13 7 13 7 21"></polyline>
                    <polyline points="7 3 7 8 15 8"></polyline>
                  </svg>
                </button>
              </div>
            ) : (
              <button
                className="study-button"
                onClick={() => {
                  setDeleteStep(0);
                  // Save scroll position before entering edit mode
                  const scrollContainer =
                    document.querySelector(".app-app") ||
                    document.querySelector(".app-main");
                  const currentScroll = scrollContainer
                    ? scrollContainer.scrollTop
                    : 0;
                  savedScrollPositionRef.current = currentScroll;
                  setEditMode(true);
                }}
                disabled={highlightMode || flashcardMode}
                style={{
                  minWidth: compactSidebar ? "40px" : "100px",
                  width: compactSidebar ? "40px" : "auto",
                  height: compactSidebar ? "40px" : "auto",
                  borderRadius: compactSidebar ? "50%" : "8px",
                  padding: compactSidebar ? "10px" : "8px 12px",
                  transition:
                    highlightMode || flashcardMode ? "none" : "all 0.3s ease",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  visibility:
                    highlightMode || flashcardMode ? "hidden" : "visible",
                }}
                title="Edit Lesson"
              >
                {compactSidebar ? <Edit2 size={18} /> : "Edit"}
              </button>
            )}
          </div>
          <div
            style={{
              position: "relative",
              minWidth: compactSidebar ? "40px" : "100px",
              height: "40px",
              marginLeft: "35px",
            }}
            onMouseLeave={() => setTimeout(() => setDeleteStep(0), 300)}
          >
            {deleteStep === 0 ? (
              <button
                className="study-button"
                onClick={() => setDeleteStep(1)}
                disabled={editMode || highlightMode || flashcardMode}
                style={{
                  minWidth: compactSidebar ? "40px" : "100px",
                  width: compactSidebar ? "40px" : "auto",
                  height: compactSidebar ? "40px" : "auto",
                  borderRadius: compactSidebar ? "50%" : "8px",
                  padding: compactSidebar ? "10px" : "8px 12px",
                  transition:
                    editMode || highlightMode || flashcardMode
                      ? "none"
                      : "all 0.3s ease",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  visibility:
                    editMode || highlightMode || flashcardMode
                      ? "hidden"
                      : "visible",
                  color: "#c62828",
                }}
                title="Delete Lesson and all its notes"
              >
                {compactSidebar ? <Trash size={18} /> : "Delete"}
              </button>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  position: "absolute",
                  top: 0,
                  left: 0,
                }}
              >
                <button
                  className="study-button"
                  onClick={() => setDeleteStep(0)}
                  style={{
                    minWidth: "40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                  title="Cancel"
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#dc3545"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                <button
                  className="study-button"
                  onClick={() => setShowDeletePrompt(true)}
                  disabled={deletingLesson}
                  style={{
                    minWidth: "40px",
                    width: "40px",
                    height: "40px",
                    borderRadius: "50%",
                    padding: "10px",
                  }}
                  title="Confirm Delete"
                >
                  {deletingLesson ? (
                    <span style={{ fontSize: "10px" }}>...</span>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#28a745"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                  )}
                </button>
              </div>
            )}
          </div>
          <div
            className="viewlesson-fc-tn-group"
            style={{
              display: "flex",
              gap: "8px",
              marginLeft: "35px",
            }}
          >
            <button
              className="study-button"
              onClick={
                flashcardMode ? handleStopFlashcards : handleCreateFlashcards
              }
              style={{
                minWidth: compactSidebar ? "40px" : "100px",
                width: compactSidebar ? "40px" : "auto",
                height: compactSidebar ? "40px" : "auto",
                borderRadius: compactSidebar ? "50%" : "8px",
                padding: compactSidebar ? "10px" : "8px 12px",
                transition:
                  editMode ||
                  highlightMode ||
                  savingFlashcards ||
                  deletingFlashcards
                    ? "none"
                    : "all 0.3s ease",
                visibility:
                  editMode ||
                  highlightMode ||
                  savingFlashcards ||
                  deletingFlashcards
                    ? "hidden"
                    : "visible",
                color: "#f59e0b",
              }}
              disabled={
                editMode ||
                highlightMode ||
                savingFlashcards ||
                deletingFlashcards
              }
              title={flashcardMode ? "Close Flashcards" : "Flashcards"}
            >
              {compactSidebar ? (
                flashcardMode ? (
                  <X size={18} />
                ) : (
                  <Layers size={18} />
                )
              ) : flashcardMode ? (
                "Close"
              ) : (
                "Flashcards"
              )}
            </button>
            <button
              className="study-button"
              onClick={highlightMode ? handleStopTakeNotes : handleTakeNotes}
              style={{
                minWidth: compactSidebar ? "40px" : "100px",
                width: compactSidebar ? "40px" : "auto",
                height: compactSidebar ? "40px" : "auto",
                borderRadius: compactSidebar ? "50%" : "8px",
                padding: compactSidebar ? "10px" : "8px 12px",
                transition:
                  editMode || flashcardMode || savingNotes || deletingNotes
                    ? "none"
                    : "all 0.3s ease",
                visibility:
                  editMode || flashcardMode || savingNotes || deletingNotes
                    ? "hidden"
                    : "visible",
                color: "#3b82f6",
              }}
              disabled={
                editMode || flashcardMode || savingNotes || deletingNotes
              }
              title={highlightMode ? "Close Take Notes" : "Take Notes"}
            >
              {compactSidebar ? (
                highlightMode ? (
                  <X size={18} />
                ) : (
                  <StickyNote size={18} />
                )
              ) : highlightMode ? (
                "Close"
              ) : (
                "Take Notes"
              )}
            </button>
          </div>
          <div className="viewlesson-share-public-group">
            {showEditorialVisibilityToggle && (
              <button
                className={`study-button viewlesson-action-button viewlesson-visibility-button ${
                  editMode || highlightMode || flashcardMode
                    ? "viewlesson-action-hidden"
                    : ""
                }`}
                onClick={handleTogglePublic}
                disabled={
                  editMode ||
                  highlightMode ||
                  flashcardMode ||
                  togglingPublic
                }
                title={article?.isPublic ? "Make private" : "Make public"}
              >
                {togglingPublic ? (
                  article?.isPublic ? (
                    <Globe size={18} />
                  ) : (
                    <Lock size={18} />
                  )
                ) : (
                  article?.isPublic ? (
                    <Globe size={18} />
                  ) : (
                    <Lock size={18} />
                  )
                )}
              </button>
            )}
            <button
              className={`study-button viewlesson-action-button ${
                editMode || highlightMode || flashcardMode
                  ? "viewlesson-action-hidden"
                  : ""
              }`}
              onClick={handleShareToCommunity}
              disabled={editMode || highlightMode || flashcardMode}
              title="Share to community"
            >
              {compactSidebar ? (
                <span className="viewlesson-action-icon-pair">
                  <Share2 size={13} />
                  <Users size={13} />
                </span>
              ) : (
                "Community"
              )}
            </button>
          </div>
        </>
      }
    >
      <div
        className={`view-lesson-container ${
          editMode || highlightMode || flashcardMode
            ? "view-lesson-container-hide-nav"
            : ""
        }`}
      >

        <NavigationButtons
          disableAll={false}
          hideCommunityButton={editMode || highlightMode || flashcardMode}
          customExitHandler={() => {
            if (highlightMode) {
              handleStopTakeNotes();
            } else {
              navigate(
                `/study/lessons?folder=${encodeURIComponent(folderFromUrl)}`,
              );
            }
          }}
        />

        {!editMode && (article.chapter || article.author) && (
          <div className="view-lesson-title">
            <div className="view-lesson-title-row">
              {article.chapter && (
                <span className="view-lesson-title-text">{article.chapter}</span>
              )}
              {articleTagChips.length > 0 && (
                <ApprovedTagChips
                  tags={articleTagChips}
                  size="small"
                  className="view-lesson-display-tags"
                  interactive={false}
                />
              )}
              {readingEstimateLabel && !highlightMode && !flashcardMode && (
                <span className="view-lesson-reading-estimate">
                  {readingEstimateLabel}
                </span>
              )}
            </div>
            {article.author && (
              <span className="view-lesson-author">{article.author}</span>
            )}
          </div>
        )}
        {editMode && (
          <div className="view-lesson-title">
            <div className="view-lesson-title-row view-lesson-title-row-edit">
              <textarea
                value={editedArticle.chapter}
                onChange={(e) => {
                  setEditedArticle({ ...editedArticle, chapter: e.target.value });
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }}
                className="view-lesson-edit-input view-lesson-title-edit-input"
                placeholder="Title"
                rows="1"
                maxLength="50"
                ref={(el) => {
                  if (el) {
                    el.style.height = "auto";
                    el.style.height = el.scrollHeight + "px";
                  }
                }}
              />
              {articleTagChips.length > 0 && (
                <ApprovedTagChips
                  tags={articleTagChips}
                  size="small"
                  className="view-lesson-display-tags"
                  interactive={false}
                />
              )}
            </div>
            <textarea
              value={editedArticle.author}
              onChange={(e) => {
                setEditedArticle({ ...editedArticle, author: e.target.value });
                e.target.style.height = "auto";
                e.target.style.height = e.target.scrollHeight + "px";
              }}
              className="view-lesson-edit-input"
              placeholder="Author"
              rows="1"
              maxLength="50"
              ref={(el) => {
                if (el) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + "px";
                }
              }}
            />
          </div>
        )}
        <div className="view-lesson-body-wrapper">
          {flashcardMode ? (
            <>
              <div
                className={
                  fullscreenFlashcards ? "flashcards-header fullscreen" : "flashcards-header"
                }
              >
                {!fullscreenFlashcards && (
                  <div className="viewlesson-flashcards-lesson-header">
                    <div className="viewlesson-flashcards-title">
                      Lesson
                    </div>
                    <button
                      className="study-button viewlesson-flashcards-toolbar-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookmarkClick();
                      }}
                      disabled={loadingBookmark}
                      title="Bookmark"
                      data-allow-navigation="true"
                    >
                      <Bookmark size={18} {...bookmarkIconProps} />
                    </button>
                  </div>
                )}
                <div
                  className={
                    fullscreenFlashcards
                      ? "flashcards-buttons-wrapper fullscreen"
                      : "flashcards-buttons-wrapper"
                  }
                >
                  <button
                    className="study-button viewlesson-add-flashcard-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      addFlashcard();
                    }}
                    title="Add Flashcard"
                  >
                    <Plus size={16} />
                  </button>
                  <span className="viewlesson-flashcards-count">
                    {flashcards.length}
                  </span>
                  <span className="viewlesson-flashcards-title">
                    Flashcards
                  </span>
                  <button
                    className="study-button viewlesson-flashcards-toolbar-button"
                    onClick={() => setFullscreenFlashcards(!fullscreenFlashcards)}
                    title={
                      fullscreenFlashcards
                        ? "Exit fullscreen flashcards"
                        : "Fullscreen flashcards"
                    }
                  >
                    {fullscreenFlashcards ? (
                      <Minimize2 size={18} />
                    ) : (
                      <Maximize2 size={18} />
                    )}
                  </button>
                  <button
                    className="study-button flashcard-button viewlesson-flashcards-toolbar-button"
                    onClick={handleCopyFromNotes}
                    title="Copy from Notes"
                  >
                    <MoveDown size={18} />
                  </button>
                  <button
                    className="study-button flashcard-button viewlesson-flashcards-toolbar-button viewlesson-save-flashcards-button"
                    onClick={handleSaveFlashcards}
                    disabled={savingFlashcards}
                    title="Save Flashcards"
                  >
                    {savingFlashcards ? "..." : <Save size={18} />}
                  </button>
                  <button
                    className="study-button view-flashcards-btn flashcard-button viewlesson-flashcards-toolbar-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (loadingFlashcardsPanel) return;
                      const hasChanges =
                        JSON.stringify(flashcards) !==
                        JSON.stringify(originalFlashcards);

                      if (hasChanges) {
                        setShouldNavigateToNotes(true);
                        setShowSaveFlashcardsPrompt(true);
                      } else {
                        navigate(
                          `/study/flashcards?folder=${encodeURIComponent(folderFromUrl)}&lessonId=${article._id}&returnTo=${encodeURIComponent(`/view/lesson/${article._id}?folder=${encodeURIComponent(folderFromUrl)}&openFlashcards=true`)}`,
                        );
                      }
                    }}
                    disabled={loadingFlashcardsPanel}
                    title="View Flashcards"
                  >
                    <Eye size={18} />
                  </button>
                  {deleteFlashcardsStep === 0 ? (
                    <button
                      className="study-button flashcard-button viewlesson-flashcards-toolbar-button viewlesson-delete-flashcards-button"
                      onClick={() => setDeleteFlashcardsStep(1)}
                      data-allow-navigation="true"
                      disabled={
                        (flashcards.length === 1 &&
                          !flashcards[0].question &&
                          !flashcards[0].answer) ||
                        savingFlashcards ||
                        deletingFlashcards
                      }
                      title="Delete All Flashcards"
                    >
                      {deletingFlashcards ? "..." : <Trash size={18} />}
                    </button>
                  ) : (
                    <div className="viewlesson-delete-flashcards-confirm-group">
                      <button
                        className="study-button flashcard-button viewlesson-flashcards-toolbar-button viewlesson-delete-flashcards-button"
                        onClick={() => setDeleteFlashcardsStep(0)}
                        data-allow-navigation="true"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                      <button
                        className="study-button flashcard-button viewlesson-flashcards-toolbar-button viewlesson-confirm-delete-flashcards-button"
                        onClick={handleConfirmDeleteAllFlashcards}
                        data-allow-navigation="true"
                        disabled={savingFlashcards || deletingFlashcards}
                        title="Confirm Delete All Flashcards"
                      >
                        <Check size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {loadingFlashcardsPanel ? (
                <div className="viewlesson-flashcards-loading">
                  Loading flashcards...
                </div>
              ) : (
                <div
                  className={
                    fullscreenFlashcards
                      ? "split-view-container fullscreen-notes"
                      : "split-view-container"
                  }
                >
                  <div className="split-view-left">
                    <div
                      className="view-lesson-body viewlesson-prewrapped-body"
                      ref={bodyRef}
                      dangerouslySetInnerHTML={{ __html: lessonBodyHtml }}
                      onClick={handleLessonBodyClick}
                    />
                  </div>
                  <div
                    className="split-view-right viewlesson-flashcards-panel"
                    ref={flashcardsContainerRef}
                  >
                    {flashcards.map((fc, index) => (
                      <div
                        key={index}
                        className="viewlesson-flashcard-editor-row"
                      >
                        <div className="viewlesson-flashcard-fields">
                          <textarea
                            placeholder="Question"
                            value={fc.question}
                            onChange={(e) => {
                              updateFlashcard(index, "question", e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height =
                                e.target.scrollHeight + "px";
                            }}
                            ref={(el) => {
                              flashcardQuestionRefs.current[index] = el;
                              if (el && fc.question) {
                                el.style.height = "auto";
                                el.style.height = el.scrollHeight + "px";
                              }
                            }}
                            rows="1"
                            className="viewlesson-flashcard-textarea"
                          />
                          <textarea
                            placeholder="Answer"
                            value={fc.answer}
                            onChange={(e) => {
                              updateFlashcard(index, "answer", e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height =
                                e.target.scrollHeight + "px";
                            }}
                            ref={(el) => {
                              flashcardAnswerRefs.current[index] = el;
                              if (el && fc.answer) {
                                el.style.height = "auto";
                                el.style.height = el.scrollHeight + "px";
                              }
                            }}
                            rows="1"
                            className="viewlesson-flashcard-textarea"
                          />
                        </div>
                        <button
                          onClick={() => removeFlashcard(index)}
                          className="viewlesson-delete-flashcard-row-button"
                          title="Delete flashcard"
                        >
                          <Trash size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : highlightMode ? (
            <>
              <div
                className={
                  fullscreenNotes ? "notes-header fullscreen" : "notes-header"
                }
              >
                {!fullscreenNotes && (
                  <div className="viewlesson-notes-lesson-header">
                    <div className="viewlesson-notes-title">
                      Lesson
                    </div>
                    <button
                      className="study-button viewlesson-notes-toolbar-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBookmarkClick();
                      }}
                      disabled={loadingBookmark}
                      title="Bookmark"
                      data-allow-navigation="true"
                    >
                      <Bookmark size={18} {...bookmarkIconProps} />
                    </button>
                  </div>
                )}
                <div
                  className={
                    fullscreenNotes
                      ? "notes-buttons-wrapper fullscreen"
                      : "notes-buttons-wrapper"
                  }
                >
                  <button
                    type="button"
                    className={`study-button viewlesson-add-note-button ${
                      notesEditMode !== "cards"
                        ? "viewlesson-add-note-button-disabled"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (notesEditMode === "cards") {
                        handleAddNote();
                      }
                    }}
                    disabled={notesEditMode !== "cards"}
                    title="Add Note"
                  >
                    <Plus size={16} />
                  </button>
                  <span className="viewlesson-notes-count">
                    {notesCount}
                  </span>
                  <span className="viewlesson-notes-title">
                    Notes
                  </span>
                  <button
                    type="button"
                    className="study-button viewlesson-notes-mode-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (notesEditMode === "cards") {
                        setNoteText(notes.join("\n\n\n"));
                        setNotesEditMode("textarea");
                      } else {
                        setCardNotes(
                          noteText ? noteText.split("\n\n\n") : [""],
                        );
                        setNotesEditMode("cards");
                      }
                    }}
                    title={
                      notesEditMode === "cards"
                        ? "Switch to textarea mode"
                        : "Switch to cards mode"
                    }
                  >
                    <span className="viewlesson-notes-mode-icon-stack" aria-hidden="true">
                      <span className="viewlesson-notes-mode-icon-main">
                        {notesEditMode === "cards" ? (
                          <StickyNote size={18} />
                        ) : (
                          <Edit2 size={18} />
                        )}
                      </span>
                      <span className="viewlesson-notes-mode-icon-switch">
                        <ArrowLeftRight size={11} />
                      </span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="study-button notes-button viewlesson-notes-toolbar-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setFullscreenNotes(!fullscreenNotes);
                    }}
                    title={
                      fullscreenNotes
                        ? "Exit fullscreen notes"
                        : "Fullscreen notes"
                    }
                  >
                    {fullscreenNotes ? (
                      <Minimize2 size={18} />
                    ) : (
                      <Maximize2 size={18} />
                    )}
                  </button>
                  <button
                    type="button"
                    className="study-button copy-to-notes-btn notes-button viewlesson-notes-toolbar-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const paragraphs = article.body
                        .split(/\n+/)
                        .filter((p) => p.trim())
                        .map((p) => p.trim())
                        .join("\n\n\n");
                      if (notesEditMode === "cards") {
                        setCardNotes((prev) => {
                          if (!paragraphs) return prev;
                          const paragraphNotes = paragraphs
                            .split("\n\n\n")
                            .filter((p) => p.trim());
                          if (prev.length === 1 && !prev[0].trim()) {
                            return paragraphNotes;
                          }
                          return [...prev, ...paragraphNotes];
                        });
                      } else {
                        setNoteText((prev) =>
                          prev ? `${prev}\n\n\n${paragraphs}` : paragraphs,
                        );
                      }
                      setLessonCopied(true);
                    }}
                    disabled={lessonCopied}
                    title="Copy Lesson to Notes"
                  >
                    <MoveDown size={18} />
                  </button>
                  {showAiNotesButton && (
                    <button
                      type="button"
                      className="study-button notes-button viewlesson-notes-toolbar-button viewlesson-ai-notes-button"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleGenerateAiNotes();
                      }}
                      data-allow-navigation="true"
                      disabled={
                        generatingAiNotes || savingNotes || loadingNotesPanel
                      }
                      title="AI Notes"
                    >
                      {generatingAiNotes ? "..." : <Sparkles size={18} />}
                    </button>
                  )}
                  <button
                    type="button"
                    className="study-button save-notes-btn notes-button viewlesson-notes-toolbar-button"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      await handleSaveNote();
                      setOriginalNoteText(currentNotesText());
                    }}
                    disabled={savingNotes}
                    title="Save Notes"
                  >
                    {savingNotes ? "..." : <Save size={18} />}
                  </button>
                  <button
                    type="button"
                    className="study-button view-notes-btn notes-button viewlesson-notes-toolbar-button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleViewNotes();
                    }}
                    disabled={loadingNotesPanel}
                    title="View Notes"
                  >
                    <Eye size={18} />
                  </button>
                  {deleteNotesStep === 0 ? (
                    <button
                      type="button"
                      className="study-button notes-button viewlesson-notes-toolbar-button viewlesson-delete-notes-button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setDeleteNotesStep(1);
                      }}
                      data-allow-navigation="true"
                      disabled={
                        !currentNotesText().trim() || savingNotes || deletingNotes
                      }
                      title="Delete All Notes"
                    >
                      {deletingNotes ? "..." : <Trash size={18} />}
                    </button>
                  ) : (
                    <div className="viewlesson-delete-notes-confirm-group">
                      <button
                        type="button"
                        className="study-button notes-button viewlesson-notes-toolbar-button viewlesson-delete-notes-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDeleteNotesStep(0);
                        }}
                        data-allow-navigation="true"
                        title="Cancel"
                      >
                        <X size={18} />
                      </button>
                      <button
                        type="button"
                        className="study-button notes-button viewlesson-notes-toolbar-button viewlesson-confirm-delete-notes-button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleConfirmDeleteAllNotes();
                        }}
                        data-allow-navigation="true"
                        disabled={savingNotes || deletingNotes}
                        title="Confirm Delete All Notes"
                      >
                        <Check size={18} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div
                className={
                  fullscreenNotes
                    ? "split-view-container fullscreen-notes"
                    : "split-view-container"
                }
              >
                <div className="split-view-left">
                  <div
                    className="view-lesson-body viewlesson-prewrapped-body"
                    ref={bodyRef}
                    dangerouslySetInnerHTML={{ __html: lessonBodyHtml }}
                    onClick={handleLessonBodyClick}
                  />
                </div>
                <div
                  className="split-view-right viewlesson-notes-panel"
                  ref={notesContainerRef}
                >
                  {loadingNotesPanel ? (
                    <div className="viewlesson-notes-loading">
                      Loading notes...
                    </div>
                  ) : notesEditMode === "textarea" ? (
                    <textarea
                      ref={notesTextareaRef}
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Your notes will appear here... 2 blank lines delimit a note"
                      className="split-view-textarea viewlesson-notes-textarea"
                    />
                  ) : (
                    <div className="viewlesson-notes-card-list">
                      {notes.map((note, index) => (
                        <div
                          key={index}
                          className="viewlesson-note-editor-row"
                        >
                          <textarea
                            value={note}
                            onChange={(e) => {
                              handleUpdateNote(index, e.target.value);
                              e.target.style.height = "auto";
                              e.target.style.height =
                                e.target.scrollHeight + "px";
                              requestAnimationFrame(() =>
                                ensureNotesTextareaVisible(e.target),
                              );
                            }}
                            onFocus={(e) => {
                              ensureNotesTextareaVisible(e.target);
                            }}
                            ref={(el) => {
                              if (el && note && !el.dataset.heightSet) {
                                el.style.height = "auto";
                                el.style.height = el.scrollHeight + "px";
                                el.dataset.heightSet = "true";
                              }
                            }}
                            rows="1"
                            className="viewlesson-note-textarea"
                          />
                          <button
                            onClick={() => handleDeleteIndividualNote(index)}
                            className="viewlesson-delete-note-row-button"
                            title="Delete note"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : editMode ? (
            <textarea
              key={article?._id}
              ref={(el) => {
                textareaRef.current = el;
                if (el && !el.dataset.initialized) {
                  el.style.height = "auto";
                  el.style.height = el.scrollHeight + "px";
                  el.dataset.initialized = "true";

                  // Focus textarea if body is empty (new lesson) and in edit mode
                  if (
                    editMode &&
                    (!article.body.trim() || article.body === " ")
                  ) {
                    el.focus();
                  }

                  // Restore scroll position on the scrollable container
                  const savedScroll = savedScrollPositionRef.current;
                  const scrollContainer =
                    document.querySelector(".app-app") ||
                    document.querySelector(".app-main");

                  if (scrollContainer) {
                    scrollContainer.scrollTop = savedScroll;
                    requestAnimationFrame(() => {
                      scrollContainer.scrollTop = savedScroll;
                    });
                  }
                }
              }}
              value={editedArticle.body}
              onChange={(e) => {
                const textarea = e.target;
                const scrollContainer =
                  document.querySelector(".app-app") ||
                  document.querySelector(".app-main");
                const caretAtEnd =
                  textarea.selectionStart === textarea.value.length;
                const currentScroll = scrollContainer
                  ? scrollContainer.scrollTop
                  : 0;

                flushSync(() => {
                  setEditedArticle({ ...editedArticle, body: e.target.value });
                });

                // Resize immediately
                textarea.style.height = "auto";
                textarea.style.height = textarea.scrollHeight + "px";

                // Force layout recalculation
                void textarea.offsetHeight;

                // Keep current scroll by default, but when typing at the end
                // ensure there's visible space under the last line.
                if (scrollContainer) {
                  const containerRect = scrollContainer.getBoundingClientRect();
                  const textareaRect = textarea.getBoundingClientRect();
                  const bottomCushion = 120;
                  const maxVisibleBottom = containerRect.bottom - bottomCushion;

                  if (caretAtEnd && textareaRect.bottom > maxVisibleBottom) {
                    scrollContainer.scrollTop +=
                      textareaRect.bottom - maxVisibleBottom;
                  } else {
                    scrollContainer.scrollTop = currentScroll;
                  }
                }
              }}
              className="view-lesson-textarea view-lesson-body-bottom-space"
              maxLength={lessonBodyLimit ?? undefined}
            />
          ) : (
            <div
              className="view-lesson-body viewlesson-prewrapped-body view-lesson-body-bottom-space"
              ref={bodyRef}
              dangerouslySetInnerHTML={{ __html: lessonBodyHtml }}
              onClick={handleLessonBodyClick}
            />
          )}
        </div>
        {editMode && showEditorialTagEditor && approvedTags.length > 0 && (
          <div className="view-lesson-edit-tags">
            <ApprovedTagChips
              tags={approvedTags}
              selectedSlugs={editedArticle.tags}
              onTagClick={handleEditorialTagToggle}
              maxSelected={EDITORIAL_LESSON_TAG_LIMIT}
              size="small"
            />
          </div>
        )}
      </div>

      {showDeletePrompt && (
        <Prompt
          question="Delete this lesson and all its notes? This action cannot be undone."
          onYes={async () => {
            setShowDeletePrompt(false);
            await handleDelete();
          }}
          onNo={() => {
            setShowDeletePrompt(false);
            setDeleteStep(0);
          }}
          yesLabel="Delete"
          noLabel="Cancel"
          yesButtonClass="button-middle button-middle-red"
          noButtonClass="button-middle button-middle-blue"
        />
      )}

      {showCancelEditPrompt && (
        <Prompt
          question="Unsaved changes"
          onYes={async () => {
            await handleSaveEdit();
            setShowCancelEditPrompt(false);
          }}
          onNo={async () => {
            setShowCancelEditPrompt(false);

            // If original body is empty, delete the lesson
            if (!article.body.trim() || article.body.trim() === " ") {
              try {
                await articlesAPI.deleteArticle(article._id);
                addToast("No lesson created", "success-info");
                navigate(returnPath);
                return;
              } catch {
                addToast("Failed to delete empty lesson", "error-warning");
              }
            }

            setEditMode(false);
            setHasUnsavedChanges(false);
            setEditedArticle({
              body: article.body,
              chapter: article.chapter || "",
              author: article.author || "",
              tags: getArticleTags(article),
            });
          }}
          onCancel={() => {
            setShowCancelEditPrompt(false);
          }}
          yesLabel="Save"
          noLabel="Don't save"
          yesButtonClass="button-middle button-middle-blue"
          noButtonClass="button-middle button-middle-red"
        />
      )}

      {showSavePrompt && (
        <Prompt
          question="Save notes?"
          onYes={async () => {
            await handleSaveNote();
            setShowSavePrompt(false);
            setOriginalNoteText(currentNotesText());

            if (shouldNavigateToNotes) {
              setShouldNavigateToNotes(false);
              navigate(
                `/study/notes?folder=${encodeURIComponent(folderFromUrl)}&type=note&lessonId=${article._id}`,
              );
            } else {
              setHighlightMode(false);
              setHighlights([]);
              setSavedSelection(null);
              
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("takeNotes");
              setSearchParams(newParams, { replace: true });
              
              await loadBookmark("lesson");
              addToast("Exited Take Notes", "success-info");
            }
          }}
          onNo={async () => {
            setShowSavePrompt(false);

            if (shouldNavigateToNotes) {
              setShouldNavigateToNotes(false);
              navigate(
                `/study/notes?folder=${encodeURIComponent(folderFromUrl)}&type=note&lessonId=${article._id}`,
              );
            } else {
              setHighlightMode(false);
              setHighlights([]);
              setSavedSelection(null);
              
              // Remove takeNotes parameter from URL
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("takeNotes");
              setSearchParams(newParams, { replace: true });
              
              await loadBookmark("lesson");
              addToast("Exited Take Notes", "success-info");
            }
          }}
          onCancel={() => {
            setShowSavePrompt(false);
            setShouldNavigateToNotes(false);
          }}
          yesLabel="Save"
          noLabel="Don't save"
          yesButtonClass="button-middle button-middle-blue"
          noButtonClass="button-middle button-middle-red"
        />
      )}

      {showSaveFlashcardsPrompt && (
        <Prompt
          question="Save flashcards?"
          onYes={async () => {
            await handleSaveFlashcards();
            setShowSaveFlashcardsPrompt(false);

            if (shouldNavigateToNotes) {
              resetFlashcardsSession();
              setShouldNavigateToNotes(false);
              navigate(
                `/study/flashcards?folder=${encodeURIComponent(folderFromUrl)}&lessonId=${article._id}&returnTo=${encodeURIComponent(`/view/lesson/${article._id}?folder=${encodeURIComponent(folderFromUrl)}&openFlashcards=true`)}`,
              );
            } else {
              resetFlashcardsSession();
              
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("flashcards");
              setSearchParams(newParams, { replace: true });
              
              await loadBookmark("lesson");
              addToast("Exited Flashcards", "success-info");
            }
          }}
          onNo={async () => {
            setShowSaveFlashcardsPrompt(false);

            if (shouldNavigateToNotes) {
              resetFlashcardsSession();
              setShouldNavigateToNotes(false);
              navigate(
                `/study/flashcards?folder=${encodeURIComponent(folderFromUrl)}&lessonId=${article._id}&returnTo=${encodeURIComponent(`/view/lesson/${article._id}?folder=${encodeURIComponent(folderFromUrl)}&openFlashcards=true`)}`,
              );
            } else {
              resetFlashcardsSession();
              
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("flashcards");
              setSearchParams(newParams, { replace: true });
              
              await loadBookmark("lesson");
              addToast("Exited Flashcards", "success-info");
            }
          }}
          onCancel={() => {
            setShowSaveFlashcardsPrompt(false);
            setShouldNavigateToNotes(false);
          }}
          yesLabel="Save"
          noLabel="Don't save"
          yesButtonClass="button-middle button-middle-blue"
          noButtonClass="button-middle button-middle-red"
        />
      )}
      <ScrollToTopButton scrollContainerSelector=".app-app" />
    </StudyLayout>
  );
};

export default ViewLesson;
